import { inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { IAuthProvider } from "ui-common/services/auth/types";
import type { ISseService } from "ui-common/services/sse/types";
import type { LinkMutatedEvent } from "ui-common/services/sse/sse-events";
import type { GetUnifiedLinksResult } from "api-contracts/rpc/get-unified-links";
import type { CreateUnifiedLinkParams } from "api-contracts/rpc/create-unified-link";
import type { UpdateUnifiedLinkParams } from "api-contracts/rpc/update-unified-link";
import type { UpdateContentTierResult } from "api-contracts/rpc/update-content-tier";
import { NotificationService } from "ui-components/notification/notification-service";
import type { IWritableBackofficeLinksState, IBackofficeLinksService } from "./types";

export class BackofficeLinksService implements IBackofficeLinksService {
  @inject<IWritableBackofficeLinksState>("writableBackofficeLinksState")
  private state!: IWritableBackofficeLinksState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthProvider>("authProvider")
  private authProvider!: IAuthProvider;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private pendingLoad: Promise<void> | undefined;
  private unsubscribeSse: (() => void) | null = null;

  loadLinks(): void {
    if (!this.authProvider.isAuthenticated()) return;
    if (this.pendingLoad) return;

    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  reloadLinks(): void {
    this.pendingLoad = undefined;
    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  private async doLoad(): Promise<void> {
    try {
      const result = await this.rpcService.call<GetUnifiedLinksResult>("getUnifiedLinks", {});
      this.state.links.set(result);
      this.subscribe();
    } catch (error) {
      console.error("[BackofficeLinksService] Error loading links:", error);
    } finally {
      this.state.loading.set(false);
      this.pendingLoad = undefined;
    }
  }

  private subscribe(): void {
    if (this.unsubscribeSse) return;

    this.unsubscribeSse = this.sseService.on<LinkMutatedEvent>("link-mutated", () => {
      this.reloadLinks();
    });
  }

  async createLink(params: CreateUnifiedLinkParams): Promise<void> {
    const result = await this.rpcService.call<{ linkId: string }>("createUnifiedLink", params);

    if (!result?.linkId) return;

    await this.handlePremiumTier(result.linkId, params.contentTier, params.oneTimePriceCents, params.title);

    await this.doLoad();
  }

  async updateLink(params: UpdateUnifiedLinkParams): Promise<void> {
    const result = await this.rpcService.call<{ linkId: string }>("updateUnifiedLink", params);

    if (!result?.linkId) return;

    if (params.contentTier === "premium") {
      await this.handlePremiumTier(result.linkId, params.contentTier, params.oneTimePriceCents, params.title);
    } else if (params.contentTier === "free") {
      await this.rpcService.call("update-content-tier", {
        contentType: "link",
        contentId: result.linkId,
        tier: "free",
      });
    }

    // Handle one-time purchase disable case
    if (params.oneTimeProductId && (!params.oneTimePriceCents || params.contentTier === "free")) {
      await this.rpcService.call("disable-one-time-purchase", {
        contentType: "link",
        contentId: result.linkId,
      });
    }

    await this.doLoad();
  }

  async deleteLink(linkId: string): Promise<void> {
    await this.rpcService.call("deleteUnifiedLink", { linkId });
    await this.doLoad();
  }

  async reorderLinks(linkIds: string[]): Promise<void> {
    const previous = this.state.links.get();

    // Optimistic update
    if (previous) {
      const reordered = linkIds
        .map((id) => previous.links.find((l) => l.id === id))
        .filter((l): l is NonNullable<typeof l> => l != null);
      this.state.links.set({ ...previous, links: reordered });
    }

    try {
      await this.rpcService.call("updateUnifiedLinksOrder", { linkIds });
      await this.doLoad();
    } catch (error) {
      this.state.links.set(previous);
      throw error;
    }
  }

  private async handlePremiumTier(
    linkId: string,
    contentTier: "free" | "premium" | undefined,
    oneTimePriceCents: number | undefined,
    title: string,
  ): Promise<void> {
    if (contentTier !== "premium") return;

    const tierResponse = await this.rpcService.call<UpdateContentTierResult>("update-content-tier", {
      contentType: "link",
      contentId: linkId,
      tier: "premium",
    });

    if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
      NotificationService.info(
        `Premium subscription enabled! Default price set to \u20AC${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
      );
    }

    if (oneTimePriceCents) {
      await this.rpcService.call("enable-one-time-purchase", {
        contentType: "link",
        contentId: linkId,
        priceCents: oneTimePriceCents,
        productName: `Premium Link - ${title.substring(0, 50)}`,
      });
    }
  }
}
